import React, { useEffect, useMemo, useRef, useState } from "react";
import { theme } from "../../styles/theme";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/joy";
import FullPageContainer from "../container/FullPageContainer";
import QRCode from "react-qr-code";
import { LogoWithTextSvg } from "static/image/common/SvgAssets";
import { useQuery } from "@tanstack/react-query";
import {
  type CustomResponseFormat,
  getOneTimeTokenStatusDetail,
  issueOneTimeToken,
  type OneTimeTokenIssueResult,
  type OneTimeTokenStatusDetail,
  setAuthHeaders,
} from "api";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { userAtom, type UserType } from "recoil/userAtom";
import { patientAtom } from "../../recoil/patientAtom";
import { userApi } from "api/userApi";
import CustomModal from "../../components/common/CustomModal";
import { isDev } from "../../config";

export default function PCLoginPage() {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    data: ott,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["pcLogin", "ott"],
    queryFn: async () => {
      const res =
        (await issueOneTimeToken()) as unknown as CustomResponseFormat<OneTimeTokenIssueResult>;
      const payload = res?.result;
      const token = payload?.oneTimeToken;
      if (!token) {
        throw new Error("Failed to get one-time token");
      }
      return token;
    },
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();
  const setUser = useSetRecoilState(userAtom);
  const setPatientState = useSetRecoilState(patientAtom);

  const [isPolling, setIsPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(120);

  useEffect(() => {
    let pollTimer: any;
    let countdownTimer: any;
    let timeoutTimer: any;

    // 초기화
    setPollError(null);
    setTimedOut(false);
    setSecondsLeft(120);

    if (!ott) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    const checkStatus = async () => {
      try {
        const res = (await getOneTimeTokenStatusDetail(
          ott
        )) as unknown as CustomResponseFormat<OneTimeTokenStatusDetail>;

        const body = res?.result;
        const idToken = body?.idToken;
        const status = body?.status;

        if (status === "expired") {
          // Stop polling immediately and show timeout UI
          setTimedOut(true);
          setIsPolling(false);
          clearInterval(pollTimer);
          clearInterval(countdownTimer);
          clearTimeout(timeoutTimer);
          return;
        }

        const authenticated = status === "verified" && !!idToken;

        if (authenticated) {
          // Stop polling immediately to avoid duplicate login attempts
          clearInterval(pollTimer);
          clearInterval(countdownTimer);
          clearTimeout(timeoutTimer);
          setIsPolling(false);
          try {
            const loginRes = (await userApi.login({
              type: "web",
              idToken: idToken,
            })) as unknown as CustomResponseFormat<UserType> & { result?: any };

            const result: any =
              (loginRes as any)?.result ?? (loginRes as any)?.data ?? loginRes;

            if (result?.accessToken) {
              setAuthHeaders(result.accessToken);
            }

            setUser(result as UserType);
            if (result?.role === "patient" && result?.userId) {
              setPatientState({
                patientId: Number(`${result.userId}`),
                nickname: result.nickname,
              });
            }

            clearInterval(pollTimer);
            clearInterval(countdownTimer);
            clearTimeout(timeoutTimer);
            setIsPolling(false);
            navigate("/", { replace: true });
          } catch (err) {
            clearInterval(pollTimer);
            clearInterval(countdownTimer);
            clearTimeout(timeoutTimer);
            setIsPolling(false);
            setPollError("로그인에 실패했어요. 다시 시도해주세요.");
          }
        }
      } catch (e: any) {
        // 상태 미완료(404/409 등)는 에러로 떨어질 수 있으므로, 2xx가 아니면 무시하고 계속 폴링.
        // 네트워크 에러가 지속되면 타임아웃에서 종료.
      }
    };

    // 2초 간격 폴링 시작
    pollTimer = setInterval(checkStatus, 2000);
    // 즉시 1회 수행하여 반응 속도 향상
    checkStatus();

    // 카운트다운 (1초 간격)
    countdownTimer = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    // 2분 타임아웃
    timeoutTimer = setTimeout(() => {
      setTimedOut(true);
      setIsPolling(false);
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
    }, 120000);

    return () => {
      clearInterval(pollTimer);
      clearInterval(countdownTimer);
      clearTimeout(timeoutTimer);
    };
  }, [ott]);

  // https://distancing.onelink.me/DeUR/6ltrqdb0
  const loginUrl = useMemo(() => {
    const token = ott ?? "";
    const base = isDev
      ? "https://distancing-dev.onelink.me/8jMV/a8qf4cey"
      : "https://distancing.onelink.me/DeUR/6ltrqdb0";
    const url = `${base}?deep_link_sub1=${encodeURIComponent(token)}`
    console.log(url)
    return url;
  }, [ott]);

  return (
    <FullPageContainer
      hasFooter={false}
      backgroundColor={theme.vars.palette.Background.Level2}
    >
      <Box sx={{ position: "absolute", top: 20, left: 20 }}>
        <LogoWithTextSvg />
      </Box>
      <Stack
        ref={containerRef}
        flexGrow={1}
        justifyContent="center"
        alignItems="center"
        gap={2}
        sx={{ width: "100%", maxWidth: "660px", mx: "auto", px: "20px" }}
      >
        {isLoading ? (
          <Stack alignItems="center" gap={1}>
            <CircularProgress size="lg" color={"neutral"} />
            <Typography
              variant="Subtitle"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
              }}
            >
              QR 생성 중...
            </Typography>
          </Stack>
        ) : isError ? (
          <Stack alignItems="center" gap={1}>
            <Typography
              variant="Subtitle"
              sx={{
                color: theme.vars.palette.Content.Medium,
                fontWeight: 500,
              }}
            >
              QR 생성 중 오류가 발생했어요.
            </Typography>
            <Button size={"md"} variant="Solid" onClick={() => refetch()}>
              다시 시도
            </Button>
          </Stack>
        ) : (
          <Stack alignItems="center" gap={4}>
            <Box width={"160px"} height={"160px"}>
              <QRCode
                value={loginUrl}
                size={160}
                style={{ display: "block" }}
                aria-label="디스턴싱 로그인용 QR 코드"
                role="img"
              />
            </Box>
            <Stack spacing={1} alignItems="center">
              <Typography
                variant="H3"
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                }}
              >
                {"디스턴싱 앱으로 로그인"}
              </Typography>
              <Typography
                variant="Subtitle"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                }}
              >
                {"휴대전화로 QR 코드를 스캔해주세요."}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
      <CustomModal
        open={timedOut}
        onClose={() => {
          setTimedOut(false);
          refetch();
        }}
        title={"로그인 실패"}
        subtitle={"새 QR 코드를 생성할게요. 다시 시도해주세요."}
        actions={[
          {
            label: "확인",
            onClick: () => {
              setTimedOut(false);
              refetch();
            },
            variant: "Solid",
          },
        ]}
      />
    </FullPageContainer>
  );
}
